// src/js/utils/mcValueFormat.ts
// Quick-task follow-up to Phase 253 — a JS mirror of
// app/Presenters/templates/Mc/detail.latte's `{define mcValueCell}` (itself a hand-copy of
// leaderboard.latte's own valueCell). Exists so the Today/Roster live-poll patchers in mc.ts and
// mc/roster.ts can render a value identically to the server's first paint, without a network
// round-trip re-rendering Latte. Zero imports throughout. The four pure formatters below
// (formatDuration/formatDistanceKm/formatCount/formatMeasuredValue) touch no DOM global either,
// mirroring utils/rosterSort.ts's own testability convention exactly, so `node tests/mc/*.test.ts`
// can load and pin them directly. `fillMcValueCell()` is the one exception — it touches `document`
// because it owns the two em-dash DOM branches (class + title), which are not expressible as a
// plain string return.
//
// THE SAME TWO DIVERGENCES mcValueCell's own docblock calls out from leaderboard.latte's valueCell
// are preserved here: baselineIncomplete is tested BEFORE value === null (Today's producer nulls
// the cell outright on a missing baseline, so on this surface the two conditions are always
// simultaneously true — testing baselineIncomplete first is what makes its dedicated copy
// reachable), and the null-cell title carries no board-position reference (nothing is ranked here).
//
// Phase 273 (D-09) — this is the FIFTH of five independent absence-title renderers this repo
// carries (the other four: detail.latte's {define mcValueCell}, leaderboard.latte's
// {define valueCell}, src/js/mc/leaderboard.ts's appendValueCell(), src/js/mc/allStats.ts's
// UNMEASURED_TITLE), widened here in lockstep with detail.latte's own widening. `fillMcValueCell()`
// gained an optional fifth `absenceTitle` parameter that overrides the null-cell title, defaulting
// to `undefined` so it falls back to `MC_VALUE_NOT_MEASURED_TITLE` unchanged — every existing
// caller (every metric that is not active_playtime) renders byte-identically.
// `MC_VALUE_NOT_MEASURED_TITLE` MUST stay byte-identical to detail.latte's `mcValueCell` default
// literal (proven in tests/mc/mc-value-format.test.ts). Both live-poll patchers — mc.ts's
// buildTodayRow() (Plan 273-13) and roster.ts's buildRosterRow() (Plan 273-10) — must pass the
// row's own reason string through this parameter, or the live poll will silently replace a
// specific D-09 sentence with this generic one 30 seconds after first paint.

export const MC_VALUE_NOT_MEASURED_TITLE =
    'Not measured for this player — no reading for this metric, not a zero.';

export const MC_VALUE_NO_BASELINE_TITLE =
    'No baseline yet — this window holds only the first daily snapshot for this player, so there '
    + 'is no earlier reading to measure a change against. This is not a measured zero.';

/** Mirrors `App\Mc\McMetrics::formatPlaytimeSeconds()` exactly — floor to whole seconds first,
 *  never a raw decimal-hours number. */
export function formatDuration(seconds: number): string {
    const whole = Math.floor(Math.max(seconds, 0));
    const hours = Math.floor(whole / 3600);
    const minutes = Math.floor((whole % 3600) / 60);
    return `${hours}h ${minutes}m`;
}

/** Mirrors `App\Mc\McMetrics::centimetersToKm()` + the template's `|number:1` filter + " km"
 *  suffix — round to 1 decimal, always show exactly 1 decimal place. */
export function formatDistanceKm(centimeters: number): string {
    const km = Math.round((centimeters / 100000) * 10) / 10;
    return `${km.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
}

/** Mirrors Latte's default `{$value|number}` filter for a plain count (blocks mined, mob kills,
 *  deaths) — grouped, no forced decimals. */
export function formatCount(value: number): string {
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** The three measured-value branches only — the two em-dash branches are DOM (title + class), not
 *  plain text, so they are built by `fillMcValueCell()` below rather than returned as a string. */
export function formatMeasuredValue(value: number, unit: string): string {
    if (unit === 'duration') return formatDuration(value);
    if (unit === 'distance_cm') return formatDistanceKm(value);
    return formatCount(value);
}

/**
 * Fills `container` with exactly what `{include mcValueCell, ...}` would have rendered into it —
 * either a plain formatted-text node, or a nested `<span class="mc-board-unmeasured...">—</span>`
 * carrying the same title Latte prints. `container` is whatever tag the caller already built
 * (`<td class="mc-board-value">` for Roster, `<span class="mc-board-value">` for Today) — this
 * function only ever touches its CHILDREN, never its class or tag.
 */
export function fillMcValueCell(
    container: HTMLElement,
    value: number | null,
    unit: string,
    baselineIncomplete = false,
    absenceTitle: string | null = null,
): void {
    container.replaceChildren();

    if (baselineIncomplete) {
        const span = document.createElement('span');
        span.className = 'mc-board-unmeasured mc-board-baseline';
        span.title = MC_VALUE_NO_BASELINE_TITLE;
        span.textContent = '—';
        container.appendChild(span);
        return;
    }

    if (value === null) {
        const span = document.createElement('span');
        span.className = 'mc-board-unmeasured';
        span.title = absenceTitle ?? MC_VALUE_NOT_MEASURED_TITLE;
        span.textContent = '—';
        container.appendChild(span);
        return;
    }

    container.textContent = formatMeasuredValue(value, unit);
}
